import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { Suspense, lazy, useLayoutEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import OAuthCallback from '@/pages/oauth/Callback'
import { usePageTracking } from '@/hooks/usePageTracking'
import { PageTitle } from '@/components/PageTitle'
import { useToastNew } from '@/hooks/useToast'
import { ToastMessage } from '@/components/ToastMessage'
import { retiredRouteTarget } from '@/routing/retiredRouteTarget'

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes)

const V1App = lazy(() => import('@/v1/V1App'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
  </div>
)

function RetiredRouteRedirect() {
  const location = useLocation()
  const target = retiredRouteTarget(location.pathname, location.search, location.hash)
  return target ? <Navigate to={target} replace /> : <NotFoundPage />
}

/**
 * A crash on one screen should not end the session.
 *
 * The boundary in main.tsx wraps the whole app including the router, so once it
 * catches anything it renders its fallback forever — the URL keeps changing as
 * the user navigates and the page never comes back. A single transient render
 * error was costing the user everything they had entered, recoverable only by a
 * manual refresh they had no reason to know would work.
 *
 * Reset only after this boundary has caught an error and the route changes.
 * Keying the boundary by location also reset errors, but it remounted the entire
 * healthy application on every navigation. V1 then re-fetched onboarding state
 * and rendered its blank guard between every onboarding step.
 */
function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation()
  const boundary = useRef<Sentry.ErrorBoundary>(null)
  const failedLocationKey = useRef<string | null>(null)

  useLayoutEffect(() => {
    if (!failedLocationKey.current || failedLocationKey.current === location.key) return
    failedLocationKey.current = null
    boundary.current?.resetErrorBoundary()
  }, [location.key])

  return (
    <Sentry.ErrorBoundary
      ref={boundary}
      fallback={RouteErrorFallback}
      onError={() => {
        failedLocationKey.current = location.key
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}

const RouteErrorFallback = () => (
  <div style={{ padding: '2rem', textAlign: 'center' }}>
    <h1>Something went wrong</h1>
    <p>The error has been reported. Go back to try again, or reload the page.</p>
    <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
      {/* A hard navigation, not router history: the boundary is rendering
          because something in the tree threw, so the safest recovery is a fresh
          document rather than another render of the same state. */}
      <button type="button" onClick={() => window.history.back()}>
        Go back
      </button>
      <button type="button" onClick={() => window.location.reload()}>
        Reload
      </button>
    </div>
  </div>
)

function App() {
  return (
    <BrowserRouter>
      <RouteErrorBoundary>
        <AppContent />
      </RouteErrorBoundary>
    </BrowserRouter>
  )
}

function AppContent() {
  usePageTracking()
  const { toastMessage, toastType } = useToastNew()

  return (
    <>
      <PageTitle />
      <div className="fixed top-4 right-4 z-50 max-w-sm">
        <ToastMessage message={toastMessage} type={toastType} />
      </div>
      <Suspense fallback={<LoadingSpinner />}>
        <SentryRoutes>
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route path="/v1/*" element={<V1App />} />
          <Route path="*" element={<RetiredRouteRedirect />} />
        </SentryRoutes>
      </Suspense>
    </>
  )
}

export default App
